import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./api";

/**
 * The SPA's replacement for Flask's `login_required` redirects. One endpoint
 * reports who is signed in; route guards read it from the query cache instead
 * of each page discovering its own auth state.
 */

export const patientSessionSchema = z.object({
  kind: z.literal("patient"),
  rm_number: z.string(),
  name: z.string(),
  avatar: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
  /** Chat/MRI credits — the header shows this so quota errors stop surprising. */
  credits_remaining: z.number().nullable().default(null),
  credits_quota: z.number().nullable().default(null),
  plan_code: z.string().nullable().default(null),
  unread_notifications: z.number().default(0),
  consent_granted: z.boolean().default(false),
});

export const adminSessionSchema = z.object({
  kind: z.literal("admin"),
  username: z.string(),
  name: z.string(),
  role: z.enum(["admin", "superadmin"]),
  avatar: z.string().nullable().default(null),
});

export const sessionSchema = z.discriminatedUnion("kind", [
  patientSessionSchema,
  adminSessionSchema,
  z.object({ kind: z.literal("anonymous") }),
]);

export type PatientSession = z.infer<typeof patientSessionSchema>;
export type AdminSession = z.infer<typeof adminSessionSchema>;
export type Session = z.infer<typeof sessionSchema>;

export const sessionQueryKey = ["session"] as const;

export function useSession() {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: ({ signal }) => api.get("/api/session", sessionSchema, signal),
    // Auth state is cheap to hold and expensive to flicker; refetch on focus
    // so a session revoked from another device is noticed promptly.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function isPatient(session: Session | undefined): session is PatientSession {
  return session?.kind === "patient";
}

export function isAdmin(session: Session | undefined): session is AdminSession {
  return session?.kind === "admin";
}
